import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFilterConfigs } from '../../../../../../hooks/useFilterConfigs';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { ExpressionType, Filter } from '../../../../../../stores/filters';

interface UseSwitchFilterControlProps {
  filterName: string;
  startingState: Record<string, boolean>;
  categories: string[];
  onChange?: (value: string) => void;
  onExpressionTypeChange?: (value: string) => void;
  onMultiFilterChange?: (filter: Filter) => void;
}

export const useSwitchFilterControl = ({
  filterName,
  startingState,
  categories,
  onChange,
  onExpressionTypeChange,
  onMultiFilterChange,
}: UseSwitchFilterControlProps) => {
  const [switchState, setSwitchState] = useState<Record<string, boolean> | null>(null);
  const [expressionType, setExpressionType] = useState<ExpressionType>('target');

  const { filters } = useFilters();
  const { filterConfigs } = useFilterConfigs();

  const filtersList = useMemo(() => {
    return filters ? (Object.values(filters) as Array<Filter>) : [];
  }, [filters]);

  const filter = useMemo(() => {
    return filtersList.find((f) => f.filterName === filterName);
  }, [filtersList, filterName]);

  const toggleableFilter = useMemo(() => {
    return filterConfigs.find((filterConfig) => filterConfig.filterName === filterName && filterConfig.toggleable);
  }, [filterName, filterConfigs]);

  const getMatchValue = useCallback(
    (filterName: string, category: string): string => {
      const filterItem = filterConfigs.find((filterConfig) => filterConfig.filterName === filterName);
      return filterItem?.type === 'button_toggles' ? 'Yes' : category;
    },
    [filterConfigs],
  );

  useEffect(() => {
    if (!filter?.expression) return;

    const { expressionType = 'target' } = filter;
    const defaultFilter: Filter = {
      filterName,
      expression: filter.expression,
      enabled: !!filter.enabled,
      expressionType,
    };

    onMultiFilterChange?.(defaultFilter);
    setExpressionType(expressionType);
  }, [filter?.expression]);

  useEffect(() => {
    if (!categories.length) return;

    if (filter?.expression) {
      const selectedCategories = filter.expression.split(',').map((item) => item.replace(/^-\s*/, '').trim());

      const updatedState = categories.reduce(
        (acc, category) => {
          const matchValue = getMatchValue(filterName, category);
          acc[category] = selectedCategories.includes(matchValue);
          return acc;
        },
        {} as Record<string, boolean>,
      );

      setSwitchState(updatedState);
    } else {
      setSwitchState(startingState);
    }
  }, [categories, filter, filterName, startingState, getMatchValue]);

  const updateFilter = useCallback(
    (updatedSwitchState: Record<string, boolean>, updatedExpressionType: ExpressionType) => {
      const toggledCategories = Object.keys(updatedSwitchState)
        .filter((key) => updatedSwitchState[key])
        .join(', ');

      onChange?.(toggledCategories);

      onMultiFilterChange?.({
        filterName,
        expression: toggledCategories,
        enabled: true,
        expressionType: updatedExpressionType,
      });
    },
    [filterName, onChange, onMultiFilterChange],
  );

  const handleToggleChange = useCallback(
    (category: string, value: boolean) => {
      if (!switchState) return;

      const newState = { ...switchState, [category]: value };
      setSwitchState(newState);

      updateFilter(newState, expressionType);
    },
    [switchState, expressionType, updateFilter],
  );

  const handleExpressionTypeChange = useCallback(
    (value: string) => {
      const updatedExpressionType = value as ExpressionType;
      setExpressionType(updatedExpressionType);
      onExpressionTypeChange?.(value);

      if (switchState) {
        updateFilter(switchState, updatedExpressionType);
      }
    },
    [switchState, onExpressionTypeChange, updateFilter],
  );

  return {
    switchState,
    toggleableFilter,
    handleToggleChange,
    handleExpressionTypeChange,
  };
};
